import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { _PortfolioProductCapability, _single } from "../../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../../core/services/dataBean/useDataBean";
import { BasicPage } from "../../../../../theme/Component/BasicPage";
import { BasicButton } from "../../../../../theme/Component/BasicButton";
import { BasicSwitch } from "../../../../../theme/Component/BasicSwitch";
import { BasicTable } from "../../../../../theme/Component/BasicTable";
import { ListMenu } from "../../../../../theme/Component/ListMenu";
import { Modal } from "../../../../../theme/Component/Modal";
import { ModalConfirm } from "../../../../../theme/Component/ModalConfirm";
import useModal, { MODAL_md, MODAL_SM } from "../../../../../theme/Component/hooks/useModal";

interface PortfolioProductCapabilityProps {
    IDPortfolioProduct: number;
    isModal?: boolean;
    _onChange?: (value: any) => void;
}

export const PortfolioProductCapability = ({ IDPortfolioProduct, isModal = false, _onChange, }: PortfolioProductCapabilityProps) => {
    const { instance, bean, setBean, lista, setLista, handleChange, addRequiredFields, validateForm, isMissing, isRequired, } = useDataBean(_PortfolioProductCapability);

    const [search, setSearch] = useState("");
    const modalForm = useModal("Capacidad del producto de portafolio", MODAL_md);
    const modalDelete = useModal("Eliminar capacidad", MODAL_SM);

    useEffect(() => {
        addRequiredFields(["IDPortfolioProduct", "Name", "Description", "Example"]);
    }, []);

    useEffect(() => {
        getList();
    }, [IDPortfolioProduct]);

    const getList = () => {
        if (!IDPortfolioProduct) {
            setLista([]);
            return;
        }

        instance.getPortfolioProductCapabilityCatalogPorPropiedades({ IDPortfolioProduct, State: 1, }, null).then(
            (response: any) => setLista(response ?? [])
        ).catch(() => toast.error("No se pudieron cargar las capacidades del producto"));
    };

    const create = () => {
        setBean({
            IDPortfolioProduct,
            Name: "",
            Description: "",
            Example: "",
            State: 1,
            IsMainCapability: false,
            IDEmployee: _single.getAccountID(),
        });
        modalForm.open("Agregar capacidad");
    };

    const save = () => {
        if (!validateForm()) {
            toast.error("Faltan campos obligatorios por diligenciar");
            return;
        }

        instance.updatePortfolioProductCapability({ ...bean, IDPortfolioProduct, State: bean.State ?? 1, IsMainCapability: bean.IsMainCapability ?? false, IDEmployee: bean.IDEmployee ?? _single.getAccountID(), }).then(
            () => {
                toast.success(bean.IDPortfolioProductCapability ? "Capacidad actualizada" : "Capacidad creada");
                modalForm.close();
                getList();
            }
        ).catch((error: any) => toast.error(error));
    };

    const remove = () => {
        instance.updatePortfolioProductCapability({ ...bean, IDPortfolioProduct, State: 2, IDEmployee: _single.getAccountID(), }).then(
            () => {
                toast.success("Capacidad eliminada");
                modalDelete.close();
                getList();
            }
        ).catch((error: any) => toast.error(error));
    };

    const selectRow = (value: any) => {
        setBean(value);
        _onChange?.(value);
    };

    const head = [
        { title: "ID", property: "IDPortfolioProductCapability", visible: true },
        { title: "Nombre de la capacidad", property: "Name", visible: true },
        { title: "Qué permite hacer", property: "Description", visible: true },
        { title: "Casos de uso", property: "Example", visible: true },
        { title: "Es capacidad principal", property: "IsMainCapability", visible: true },
    ];

    const rowActions = () => (
        <ListMenu listaBotones={[
            { titulo: "Editar", icono: "ri-edit-2-line", visible: _single.canEdit(), evento: () => modalForm.open(`Editar ${bean.Name ?? "capacidad"}`), },
            { titulo: "Eliminar", icono: "ri-delete-bin-line", visible: _single.canDelete(), evento: () => modalDelete.open(), },
        ]} />
    );

    const content = (
        <div className="row">
            <div className="col-md-12">
                <BasicTable
                    body={lista}
                    head={head}
                    rowSet={selectRow}
                    listButtons={rowActions()}
                    iconButtons={[
                        { nombre: "Agregar", icono: "ri-file-add-line", visible: _single.canCreate(), evento: create },
                    ]}
                />
            </div>

            <Modal modal={modalForm.modal} updateModal={modalForm.setModal} eventModal={save}>
                <div className="row">
                    <div className="col-md-12">
                        <label className={isMissing("Name") ? "has-error" : ""}>
                            Nombre de la capacidad {isRequired("Name") && <span className="text-danger">*</span>}
                        </label>
                        <input name="Name" className="form-control" value={bean.Name ?? ""} onChange={handleChange} />
                    </div>
                    <div className="col-md-12">
                        <label className={isMissing("Description") ? "has-error" : ""}>
                            Qué permite hacer {isRequired("Description") && <span className="text-danger">*</span>}
                        </label>
                        <textarea name="Description" className="form-control" value={bean.Description ?? ""} onChange={handleChange} />
                    </div>
                    <div className="col-md-12">
                        <label className={isMissing("Example") ? "has-error" : ""}>
                            Casos de uso {isRequired("Example") && <span className="text-danger">*</span>}
                        </label>
                        <textarea name="Example" className="form-control" value={bean.Example ?? ""} onChange={handleChange} />
                    </div>
                    <div className="col-md-12">
                        <BasicSwitch
                            label="Es capacidad principal"
                            estado={bean.IsMainCapability ?? false}
                            eventChange={(value) => setBean({ ...bean, IsMainCapability: value })}
                        />
                    </div>
                </div>
            </Modal>

            <ModalConfirm modal={modalDelete.modal} updateModal={modalDelete.setModal} eventModal={remove}>
                <strong>¿Eliminar la capacidad {bean.Name}?</strong>
            </ModalConfirm>
        </div>
    );

    return isModal ? content : (
        <BasicPage titulo="Capacidades del producto de portafolio">
            {content}
        </BasicPage>
    );
};
